import { Link } from '@tanstack/react-router';
import { HelpCircle } from '@untitled-ui/icons-react';
import { type ReactNode, isValidElement } from 'react';
import { twMerge } from 'tailwind-merge';

import { getConfig } from '../config';
import type { Brand } from '../entity/api/brand';
import type { Organization } from '../entity/api/organization';
import { type OrganizationBilling, PlanStatus } from '../entity/api/organization-billing';
import { OrganizationUserRole } from '../entity/api/organization-user';
import { getQueryClient } from '../sdk/request';
import {
  getAuthUserFromCache,
  useOrganizationBilling,
  useOrganizationUsersByUserId,
} from '../services/storage';
import { tGlobal } from '../services/translation';
import { getOrgCredit } from '../shared/credits';
import { Feature } from '../shared/feature';
import { getSelectedOrganization } from '../shared/organization';
import { CreditSidebar } from './credit-sidebar';
import { Blym } from './decorations/blym';
import { FeatureFlag } from './feature-flag';
import { NavItem } from './sidebar-nav-item';

export function SidebarNavSection(props: {
  children: ReactNode;
  title: string;
  className?: string;
}) {
  return (
    <div className={twMerge(props.className ?? '')}>
      <p className="font-body-3-m mb-2 px-2 py-1.5 text-dark-gray">{props.title}</p>
      <div className="grid grid-flow-row grid-cols-1 gap-1">{props.children}</div>
    </div>
  );
}

export type AppSidebarProps = {
  className?: string;
  currentPath: string;
  currentOrganization?: Organization;
  currentOrgBilling?: OrganizationBilling;
  currentBrand?: Brand;
  navigator: ReactNode;
  navSections: ReactNode[];
  footer: ReactNode;
  variant?: 'desktop' | 'mobile';
};
export function AppSidebar(props: AppSidebarProps) {
  const { currentOrganization, currentBrand } = props;
  const queryClient = getQueryClient();

  // TanStack Query hooks
  const currentUser = getAuthUserFromCache(queryClient);
  const currentOrg = getSelectedOrganization(currentUser);

  // TanStack Query for org users by user ID
  const { data: userOrgUsersData } = useOrganizationUsersByUserId(currentUser?._id);
  const currentOrgUser = userOrgUsersData?.find(
    (orgUser) => orgUser?.organizationId === currentOrg?._id
  );

  // TanStack Query for organization billing
  const { data: orgBilling } = useOrganizationBilling(currentOrg?._id);
  const { available, total } = getOrgCredit(orgBilling);
  const extraCreditCount = orgBilling?.plan?.extraCreditCount ?? 0;

  // Check if product features should be shown
  const productType = currentBrand?.productType;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, prefer-destructuring
  const shouldShowProductFeatures =
    productType !== undefined &&
    productType !== null &&
    orgBilling?.currentPlanStatus != PlanStatus.NotSubscribed &&
    orgBilling?.currentPlanStatus != PlanStatus.Cancelled;

  // NOTE: This is a hacky way to have smooth transition between the sidebar and the setting sidebar.
  // Check if we're truly in settings view by checking if navigator is a Link with back button
  // This prevents the blink when URL changes but content hasn't transitioned yet
  const isInSettingsView =
    isValidElement(props.navigator) &&
    props.navigator.type === Link &&
    (props.navigator.props as Record<string, unknown>).to === '/app';

  const isMobile = props.variant === 'mobile';
  const baseClasses = 'flex h-full auto-rows-min flex-col bg-neutral-50 px-2 py-6';
  const desktopClasses = 'min-w-dashboardSidebar max-w-dashboardSidebar';
  const mobileClasses = 'w-full';

  return (
    <div
      className={twMerge(
        baseClasses,
        isMobile ? mobileClasses : desktopClasses,
        props.className ?? ''
      )}
    >
      {isInSettingsView ? null : (
        <div className="mb-6 inline-block h-[25px]">
          <Link className="inline-block" to="/app" aria-label={tGlobal('goToDashboard')}>
            <Blym />
          </Link>
        </div>
      )}
      {props.navigator}
      <div className="grid grid-flow-row grid-cols-1 place-content-start gap-6">
        {props.navSections}
      </div>
      <div className="mt-auto grid grid-flow-row grid-cols-1 gap-2" data-testid="sidebar-user-btn">
        {shouldShowProductFeatures ? (
          <FeatureFlag flags={[Feature.SUBSCRIPTION]}>
            {currentOrgUser?.role === OrganizationUserRole.Owner ? (
              <Link
                to="/app/settings/organization/$id/plan"
                params={{ id: currentOrganization?._id ?? '' }}
                className="mb-4 w-full"
              >
                <CreditSidebar
                  availableCredits={available}
                  totalCredits={total}
                  extraCredits={extraCreditCount}
                />
              </Link>
            ) : (
              <CreditSidebar
                availableCredits={available}
                extraCredits={extraCreditCount}
                totalCredits={total}
                className="mb-4 w-full"
              />
            )}
          </FeatureFlag>
        ) : null}
        {props.footer}
        <NavItem
          label={tGlobal('getHelp')}
          icon={<HelpCircle width={16} height={16} />}
          href={getConfig().urlHelp}
          target={'_blank'}
        />
      </div>
    </div>
  );
}
